import { useState, useEffect, useCallback } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import { coursesAPI, applicationsAPI, favoritesAPI } from '@/lib/api';
import api from '@/lib/api';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import PreApplyRequirementsDialog from '@/components/PreApplyRequirementsDialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import {
  Search,
  Filter,
  Clock,
  MapPin,
  Building,
  GraduationCap,
  Loader2,
  BookOpen,
  ExternalLink,
  Sparkles,
  ThumbsUp,
  X,
  Heart,
  ChevronRight
} from 'lucide-react';

const CoursesPage = () => {
  const { isAuthenticated, user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [courses, setCourses] = useState([]);
  const [applying, setApplying] = useState(null);
  const [preApplyCourse, setPreApplyCourse] = useState(null);
  const [preApplyUniversity, setPreApplyUniversity] = useState(null);
  const [preApplyOpen, setPreApplyOpen] = useState(false);
  const [similarCourses, setSimilarCourses] = useState([]);
  const [showSimilar, setShowSimilar] = useState(false);
  const [loadingSimilar, setLoadingSimilar] = useState(false);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [favoriteIds, setFavoriteIds] = useState(new Set());
  const [togglingFav, setTogglingFav] = useState(null);
  
  const [filters, setFilters] = useState({
    search: searchParams.get('search') || '',
    country: searchParams.get('country') || '',
    degree_level: searchParams.get('level') || '',
    field_of_study: searchParams.get('field') || '',
    budget_max: searchParams.get('budget') || ''
  });

  // Countries list - using consistent naming (United Kingdom, not UK)
  // Pulled dynamically from the live destinations endpoint so newly-added
  // countries (e.g. Turkey, New Zealand) appear here automatically.
  const [countries, setCountries] = useState([
    'United Kingdom', 'USA', 'Canada', 'Ireland', 'France', 'Germany',
    'Netherlands', 'Australia', 'New Zealand', 'Turkey',
  ]);
  useEffect(() => {
    let cancelled = false;
    api.get('/destinations').then((res) => {
      if (cancelled) return;
      const list = (res.data || [])
        .map((d) => d?.country)
        .filter(Boolean);
      if (list.length) setCountries(list);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, []);
  const degreeLevels = ['Bachelors', 'Masters', 'PhD', 'Foundation', 'Pre-Masters'];
  const fields = [
    'Business & Management', 
    'Computer Science & IT', 
    'Engineering', 
    'Healthcare & Medicine', 
    'Law', 
    'Psychology & Social Sciences',
    'Supply Chain & Operations',
    'Hospitality & Tourism',
    'Environmental Sciences',
    'English & Languages',
    'General Studies'
  ];
  const budgetRanges = [
    { value: 'any', label: 'Any Budget' },
    { value: '20000', label: 'Under £20,000' },
    { value: '30000', label: 'Under £30,000' },
    { value: '50000', label: 'Under £50,000' }
  ];

  const fetchCourses = useCallback(async (params = {}) => {
    setLoading(true);
    try {
      const response = await coursesAPI.getAll({
        search: params.search || undefined,
        country: params.country || undefined,
        degree_level: params.degree_level || undefined,
        field_of_study: params.field_of_study || undefined,
        budget_max: params.budget_max || undefined
      });
      setCourses(response.data || []);
    } catch (error) {
      console.error('Error fetching courses:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCourses();
  }, [fetchCourses]);

  // Load current wishlist (students only) so we can highlight heart icons
  useEffect(() => {
    if (!isAuthenticated || user?.role !== 'student') return;
    (async () => {
      try {
        const res = await favoritesAPI.getAll();
        const ids = new Set((res.data || []).map(f => f.course?.id).filter(Boolean));
        setFavoriteIds(ids);
      } catch (e) { /* swallow — wishlist is non-critical */ }
    })();
  }, [isAuthenticated, user?.role]);

  const toggleFavorite = async (courseId) => {
    if (!isAuthenticated) {
      toast.error('Please login to add to your wishlist');
      return;
    }
    if (user?.role !== 'student') {
      toast.error('Only students can use the wishlist');
      return;
    }
    setTogglingFav(courseId);
    try {
      if (favoriteIds.has(courseId)) {
        await favoritesAPI.remove(courseId);
        setFavoriteIds(prev => {
          const next = new Set(prev);
          next.delete(courseId);
          return next;
        });
        toast.success('Removed from wishlist');
      } else {
        await favoritesAPI.add(courseId);
        setFavoriteIds(prev => new Set(prev).add(courseId));
        toast.success('Added to wishlist');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update wishlist');
    } finally {
      setTogglingFav(null);
    }
  };

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (filters.search !== undefined) {
        fetchCourses(filters);
      }
    }, 400);
    return () => clearTimeout(timer);
  }, [filters.search, fetchCourses]);

  const handleFilterChange = (key, value) => {
    const newFilters = { ...filters, [key]: value };
    setFilters(newFilters);
    if (key !== 'search') {
      fetchCourses(newFilters);
    }
  };

  const handleApply = async (courseOrId) => {
    if (!isAuthenticated) {
      toast.error('Please login to apply for courses');
      return;
    }
    if (user?.role !== 'student') {
      toast.error('Only students can apply for courses');
      return;
    }

    // Resolve the course object so we can pull university details for the
    // pre-apply requirements dialog.
    const course = typeof courseOrId === 'string'
      ? courses.find((c) => c.id === courseOrId)
      : courseOrId;
    if (!course) {
      toast.error('Course not found');
      return;
    }

    // Fetch the university (with pakistan_requirements) for this course.
    let uni = null;
    try {
      if (course.university_id) {
        const res = await api.get(`/universities/${course.university_id}`);
        uni = res.data || null;
      }
    } catch (e) {
      // Fall through with the limited info we already have on the course card.
      uni = { name: course.university_name, country: course.country };
    }

    setPreApplyCourse(course);
    setPreApplyUniversity(uni || { name: course.university_name, country: course.country });
    setPreApplyOpen(true);
  };

  const submitApplication = async () => {
    if (!preApplyCourse) return;
    setApplying(preApplyCourse.id);
    try {
      await applicationsAPI.create({ course_id: preApplyCourse.id });
      toast.success('Application submitted successfully!');
      setPreApplyOpen(false);
      setPreApplyCourse(null);
      setPreApplyUniversity(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit application');
    } finally {
      setApplying(null);
    }
  };

  const handleViewSimilar = async (course) => {
    setSelectedCourse(course);
    setShowSimilar(true);
    setLoadingSimilar(true);
    try {
      const res = await coursesAPI.getSimilar(course.id, 6);
      setSimilarCourses(res.data?.similar_courses || []);
    } catch (error) {
      console.error('Failed to load similar courses:', error);
      setSimilarCourses([]);
    } finally {
      setLoadingSimilar(false);
    }
  };

  const formatCurrency = (amount, currency, country) => {
    // Map country to currency if not provided
    const countryCurrencyMap = {
      'UK': 'GBP',
      'United Kingdom': 'GBP',
      'USA': 'USD',
      'United States': 'USD',
      'Canada': 'CAD',
      'Australia': 'AUD',
      'Germany': 'EUR',
      'France': 'EUR',
      'Netherlands': 'EUR',
      'Ireland': 'EUR',
      'New Zealand': 'NZD'
    };
    
    const currencyCode = currency || countryCurrencyMap[country] || 'GBP';
    const locale = currencyCode === 'GBP' ? 'en-GB' : currencyCode === 'EUR' ? 'de-DE' : 'en-US';
    
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currencyCode,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const getCurrencySymbol = (currency, country) => {
    const countryCurrencyMap = {
      'UK': 'GBP', 'United Kingdom': 'GBP',
      'USA': 'USD', 'United States': 'USD',
      'Canada': 'CAD', 'Australia': 'AUD',
      'Germany': 'EUR', 'France': 'EUR', 'Netherlands': 'EUR', 'Ireland': 'EUR',
      'New Zealand': 'NZD'
    };
    const currencyCode = currency || countryCurrencyMap[country] || 'GBP';
    const symbols = { 'GBP': '£', 'USD': '$', 'EUR': '€', 'CAD': 'C$', 'AUD': 'A$', 'NZD': 'NZ$' };
    return symbols[currencyCode] || '£';
  };

  const filteredCourses = courses;

  return (
    <div className="min-h-screen bg-[#f8f9fa] dark:bg-slate-900 transition-colors">
      <Navbar />
      
      <main className="pt-20 pb-12">
        {/* Hero Section */}
        <div className="bg-gradient-to-r from-teal-50 to-cyan-50 dark:from-slate-800 dark:to-slate-800 py-12 transition-colors">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h1 className="text-4xl font-semibold text-gray-900 dark:text-white mb-4">Find Your Perfect Course</h1>
            <p className="text-gray-600 dark:text-gray-400 text-lg mb-8">
              Explore 13,800+ courses from top universities worldwide
            </p>
            
            {/* Search Bar */}
            <div className="relative max-w-2xl">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500 dark:text-gray-400" />
              <Input
                type="text"
                placeholder="Search courses or universities..."
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                className="pl-12 h-14 bg-white dark:bg-slate-800 border-gray-200 dark:border-slate-600 focus:border-teal-400 text-lg dark:text-white"
                data-testid="course-search"
              />
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Filters - Bordered Section */}
          <div className="bg-white dark:bg-slate-800 border-2 border-gray-200 dark:border-slate-700 rounded-2xl p-6 mb-6 transition-colors">
            <div className="flex items-center gap-3 mb-5 pb-4 border-b border-gray-200 dark:border-slate-600">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-teal-500 to-teal-600 flex items-center justify-center">
                <Filter className="w-5 h-5 text-white" />
              </div>
              <div className="flex-1">
                <h2 className="text-lg font-semibold text-gray-800 dark:text-white">Filter Courses</h2>
                <p className="text-sm text-gray-500 dark:text-gray-400">Narrow down your search</p>
              </div>
              {(filters.country || filters.degree_level || filters.field_of_study || filters.budget_max) && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setFilters({ search: '', country: '', degree_level: '', field_of_study: '', budget_max: '' });
                    fetchCourses({});
                  }}
                  className="text-teal-600 dark:text-teal-400 hover:text-teal-700 dark:hover:text-teal-300"
                >
                  <X className="w-4 h-4 mr-1" />
                  Clear All
                </Button>
              )}
            </div>
            
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <Select value={filters.country || 'all'} onValueChange={(v) => handleFilterChange('country', v === 'all' ? '' : v)}>
                <SelectTrigger className="w-full bg-gray-50 dark:bg-slate-700 border-gray-200 dark:border-slate-600 h-11 text-gray-800 dark:text-white" data-testid="filter-country">
                  <SelectValue placeholder="Country" />
                </SelectTrigger>
                <SelectContent className="bg-white dark:bg-slate-800 border-gray-200 dark:border-slate-600">
                  <SelectItem value="all">All Countries</SelectItem>
                  {countries.map(c => (
                    <SelectItem key={c} value={c}>{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={filters.degree_level || 'all'} onValueChange={(v) => handleFilterChange('degree_level', v === 'all' ? '' : v)}>
                <SelectTrigger className="w-full bg-gray-50 dark:bg-slate-700 border-gray-200 dark:border-slate-600 h-11 text-gray-800 dark:text-white" data-testid="filter-level">
                  <SelectValue placeholder="Level" />
                </SelectTrigger>
                <SelectContent className="bg-white dark:bg-slate-800 border-gray-200 dark:border-slate-600">
                  <SelectItem value="all">All Levels</SelectItem>
                  {degreeLevels.map(l => (
                    <SelectItem key={l} value={l} className="capitalize">{l}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={filters.field_of_study || 'all'} onValueChange={(v) => handleFilterChange('field_of_study', v === 'all' ? '' : v)}>
                <SelectTrigger className="w-full bg-gray-50 dark:bg-slate-700 border-gray-200 dark:border-slate-600 h-11 text-gray-800 dark:text-white" data-testid="filter-field">
                  <SelectValue placeholder="Field" />
                </SelectTrigger>
                <SelectContent className="bg-white dark:bg-slate-800 border-gray-200 dark:border-slate-600">
                  <SelectItem value="all">All Fields</SelectItem>
                  {fields.map(f => (
                    <SelectItem key={f} value={f}>{f}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={filters.budget_max || 'any'} onValueChange={(v) => handleFilterChange('budget_max', v === 'any' ? '' : v)}>
                <SelectTrigger className="w-full bg-gray-50 dark:bg-slate-700 border-gray-200 dark:border-slate-600 h-11 text-gray-800 dark:text-white" data-testid="filter-budget">
                  <SelectValue placeholder="Budget" />
                </SelectTrigger>
                <SelectContent className="bg-white dark:bg-slate-800 border-gray-200 dark:border-slate-600">
                  {budgetRanges.map(b => (
                    <SelectItem key={b.value} value={b.value}>{b.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Results */}
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-8 h-8 text-teal-500 animate-spin" />
            </div>
          ) : (
            <>
              <p className="text-gray-600 mb-6">{filteredCourses.length} courses found</p>
              
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredCourses.map((course) => (
                  <div 
                    key={course.id} 
                    className="bg-white dark:bg-slate-800 rounded-xl p-6 border-2 border-gray-200 dark:border-slate-700 transition-colors hover:border-teal-400/50 hover:shadow-lg transition-all group"
                    data-testid={`course-${course.id}`}
                  >
                    <div className="flex items-start justify-between mb-3">
                      <Badge variant="outline" className="border-teal-400/50 text-teal-600 capitalize">
                        {course.degree_level}
                      </Badge>
                      <span className="text-sm text-gray-500 flex items-center gap-1">
                        <MapPin className="w-3 h-3" />
                        {course.country}
                      </span>
                    </div>
                    
                    <h3 className="text-lg font-semibold text-gray-900 mb-2 group-hover:text-teal-600 transition-colors truncate" title={course.name}>
                      {course.name}
                    </h3>
                    
                    <div className="flex items-center gap-2 mb-4 flex-wrap">
                      <Link 
                        to={`/university/${course.university_id}`}
                        className="text-gray-600 flex items-center gap-1 hover:text-teal-600 transition-colors min-w-0"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Building className="w-4 h-4 flex-shrink-0" />
                        <span className="hover:underline truncate">{course.university_name}</span>
                      </Link>
                      {course.course_url && (
                        <a
                          href={course.course_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-green-600 hover:text-green-700 flex items-center gap-1 text-xs bg-green-400/10 px-2 py-1 rounded-full"
                          onClick={(e) => e.stopPropagation()}
                          title={`View this exact course on ${course.university_name} official website`}
                        >
                          <ExternalLink className="w-3 h-3" />
                          Verified Link
                        </a>
                      )}
                    </div>

                    <div className="flex items-center gap-4 text-sm text-gray-500 mb-4">
                      <span className="flex items-center gap-1">
                        <Clock className="w-4 h-4" />
                        {course.duration_months} months
                      </span>
                      <span className="flex items-center gap-1">
                        <BookOpen className="w-4 h-4" />
                        {course.field_of_study}
                      </span>
                    </div>

                    <div className="flex items-center gap-2 mb-4">
                      <span className="text-xl font-semibold text-teal-600">
                        {formatCurrency(course.tuition_fee, course.currency, course.country)}
                      </span>
                      <span className="text-gray-500 text-sm">/year</span>
                    </div>

                    {course.ielts_requirement && (
                      <p className="text-sm text-gray-500 mb-4">
                        IELTS: {course.ielts_requirement}+ required
                      </p>
                    )}

                    <div className="flex flex-wrap gap-2 mb-4">
                      {(Array.isArray(course.intake_dates) 
                        ? course.intake_dates.slice(0, 2) 
                        : (course.intake_dates || '').split(',').slice(0, 2)
                      ).map((date, idx) => (
                        <Badge key={`${`+var_name+`}-${idx}`} variant="outline" className="border-gray-300 text-gray-600 text-xs">
                          {date.trim()}
                        </Badge>
                      ))}
                    </div>

                    <div className="flex gap-2">
                      <Button
                        onClick={() => handleApply(course.id)}
                        disabled={applying === course.id}
                        className="flex-1 bg-teal-50 text-teal-600 hover:bg-teal-500 hover:text-gray-800 font-medium transition-all"
                        data-testid={`apply-btn-${course.id}`}
                      >
                        {applying === course.id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          'Apply Now'
                        )}
                      </Button>
                      <Button
                        onClick={() => toggleFavorite(course.id)}
                        disabled={togglingFav === course.id}
                        variant="outline"
                        title={favoriteIds.has(course.id) ? 'Remove from wishlist' : 'Add to wishlist'}
                        className={`px-3 ${favoriteIds.has(course.id) ? 'bg-rose-50 text-rose-600 border-rose-200 hover:bg-rose-100' : 'text-gray-500 hover:text-rose-600'}`}
                        data-testid={`wishlist-btn-${course.id}`}
                      >
                        {togglingFav === course.id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Heart className={`w-4 h-4 ${favoriteIds.has(course.id) ? 'fill-current' : ''}`} />
                        )}
                      </Button>
                    </div>
                    
                    <Button
                      onClick={() => handleViewSimilar(course)}
                      variant="ghost"
                      className="w-full mt-2 text-gray-500 hover:text-teal-600 text-sm"
                      data-testid={`similar-btn-${course.id}`}
                    >
                      <Sparkles className="w-4 h-4 mr-2" />
                      View Similar Courses
                    </Button>
                  </div>
                ))}
              </div>

              {filteredCourses.length === 0 && (
                <div className="text-center py-20">
                  <GraduationCap className="w-16 h-16 text-gray-500 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">No courses found</h3>
                  <p className="text-gray-500 dark:text-gray-400">Try adjusting your filters or search terms</p>
                </div>
              )}
            </>
          )}
        </div>
      </main>

      <Footer />

      {/* Similar Courses Dialog */}
      <Dialog open={showSimilar} onOpenChange={setShowSimilar}>
        <DialogContent className="bg-white border-gray-200 max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-gray-900 flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-teal-500" />
              Students Who Viewed This Also Liked
            </DialogTitle>
          </DialogHeader>
          
          {selectedCourse && (
            <div className="mb-4 p-3 bg-teal-50 rounded-lg border border-teal-200">
              <p className="text-sm text-gray-600 dark:text-gray-300">Based on your interest in:</p>
              <p className="text-gray-900 font-medium">{selectedCourse.name}</p>
              <p className="text-xs text-gray-500 dark:text-gray-400">{selectedCourse.university_name} • {selectedCourse.country}</p>
            </div>
          )}
          
          {loadingSimilar ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 text-teal-500 animate-spin" />
            </div>
          ) : similarCourses.length > 0 ? (
            <div className="grid sm:grid-cols-2 gap-4">
              {similarCourses.map((course) => (
                <div key={course.id} className="bg-[#f8f9fa] rounded-lg p-4 border border-gray-200 hover:border-teal-400/50 transition-colors">
                  <div className="flex items-start justify-between mb-2">
                    <Badge variant="outline" className="border-teal-400/50 text-teal-600 text-xs">
                      {course.degree_level}
                    </Badge>
                    {course.similarity_score && (
                      <Badge className="bg-green-100 text-green-700 text-xs">
                        <ThumbsUp className="w-3 h-3 mr-1" />
                        {course.similarity_score}% match
                      </Badge>
                    )}
                  </div>
                  <h4 className="text-gray-900 font-medium mb-1 line-clamp-2">{course.name}</h4>
                  <p className="text-gray-600 text-sm mb-2">{course.university_name}</p>
                  
                  {course.similarity_reasons && (
                    <div className="flex flex-wrap gap-1 mb-3">
                      {course.similarity_reasons.map((reason, idx) => (
                        <span key={`${`+var_name+`}-${idx}`} className="text-xs bg-gray-200 text-gray-700 px-2 py-0.5 rounded">
                          {reason}
                        </span>
                      ))}
                    </div>
                  )}
                  
                  <div className="flex items-center justify-between">
                    <span className="text-teal-600 font-semibold text-sm">
                      {formatCurrency(course.tuition_fee, course.currency, course.country)}
                    </span>
                    <Button
                      onClick={() => {
                        setShowSimilar(false);
                        handleApply(course.id);
                      }}
                      size="sm"
                      className="bg-teal-50 text-teal-600 hover:bg-teal-500 hover:text-gray-800 dark:text-white"
                    >
                      Apply <ChevronRight className="w-3 h-3 ml-1" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <p className="text-gray-500 dark:text-gray-400">No similar courses found</p>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <PreApplyRequirementsDialog
        open={preApplyOpen}
        onOpenChange={setPreApplyOpen}
        university={preApplyUniversity}
        course={preApplyCourse}
        submitting={applying === preApplyCourse?.id}
        onConfirm={submitApplication}
      />
    </div>
  );
};

export default CoursesPage;
